import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { xhrRequest } from '../lib/xhrRequest';
import { toast } from 'sonner';
import {
  Trophy, TrendingUp, Home, List, User, Loader2,
  Sparkles, Info, ShieldCheck, Lock, DollarSign, History,
  AlertTriangle, CheckCircle,
} from 'lucide-react';
import Footer from '../components/Footer';

const formatCents = (c) =>
  `$${(Math.max(0, c) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function RankRow({ entry, showPayout }) {
  const medal = ['🥇', '🥈', '🥉'][entry.rank - 1] || '';
  return (
    <div
      className="flex items-center gap-3 py-3 border-b border-white/5 last:border-b-0"
      data-testid={`rank-row-${entry.rank}`}
    >
      <span className="text-2xl w-8 text-center" aria-label={`Rank ${entry.rank}`}>{medal}</span>
      <div className="w-10 h-10 rounded-xl bg-white/10 overflow-hidden flex items-center justify-center">
        {entry.photoUrl ? (
          <img src={entry.photoUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="font-bold">{entry.displayName?.[0]?.toUpperCase()}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{entry.displayName}</p>
        <p className="text-xs text-[#A1A1AA] truncate">{entry.serviceTitle}</p>
      </div>
      <div className="text-right">
        <p className="font-mono text-sm text-white">{formatCents(entry.totalCents)}</p>
        <p className="text-xs text-[#A1A1AA]">avg {formatCents(entry.avgCents)}</p>
      </div>
      {showPayout && (
        <div className="text-right pl-3 border-l border-white/10 min-w-[80px]">
          <p className="font-mono text-sm text-[#F5C542]">{formatCents(entry.payoutCents)}</p>
          <p className="text-xs text-[#A1A1AA]">payout</p>
        </div>
      )}
    </div>
  );
}

/**
 * Admin action panel. Two clearly-separated actions:
 *   1. Close period  -> freezes standings + pool for a paid-out snapshot
 *   2. Pay out       -> creates Stripe Transfers to each winner
 * Each action requires a confirmation click to prevent accidental fires.
 */
function AdminPanel({ periodKey, poolCents, status, onDone }) {
  const { token } = useAuth();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [busy, setBusy] = useState(null); // 'close' | 'payout' | null
  const [confirming, setConfirming] = useState(null); // 'close' | 'payout' | null
  const [lastResult, setLastResult] = useState(null);

  const runClose = useCallback(async () => {
    setBusy('close');
    setConfirming(null);
    const { ok, data } = await xhrRequest('POST', `${API}/challenge/close-period`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ periodKey }),
    });
    setBusy(null);
    if (!ok) return toast.error(data.detail || 'Could not close period');
    toast.success(`Period ${periodKey} closed`);
    setLastResult({ kind: 'close', period: data });
    onDone?.();
  }, [API, token, periodKey, onDone]);

  const runPayout = useCallback(async () => {
    setBusy('payout');
    setConfirming(null);
    const { ok, data } = await xhrRequest('POST', `${API}/challenge/payout`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ periodKey }),
    });
    setBusy(null);
    if (!ok) return toast.error(data.detail || 'Payout failed');
    setLastResult({ kind: 'payout', period: data });
    const stat = data.status;
    if (stat === 'paid') toast.success('All winners paid');
    else if (stat === 'partially_paid') toast.warning('Payout partially completed');
    else toast.error('Payout failed');
    onDone?.();
  }, [API, token, periodKey, onDone]);

  const isOpen = status === 'open';
  const isClosed = status === 'closed';
  const isFinal = status === 'paid' || status === 'partially_paid' || status === 'payout_failed';

  return (
    <section
      className="stat-card border-[#F5C542]/40 bg-[#F5C542]/[0.03]"
      data-testid="admin-panel"
    >
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} className="text-[#F5C542]" />
        <h3 className="font-heading font-semibold">Admin actions</h3>
      </div>
      <p className="text-xs text-[#A1A1AA] mb-4">
        Two-step flow: <b>close</b> freezes the standings, then <b>pay out</b> transfers real money.
      </p>

      {/* Step 1: Close */}
      <div className="rounded-xl bg-black/40 border border-white/5 p-4 mb-3" data-testid="admin-close-block">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-semibold text-sm flex items-center gap-1.5">
              <Lock size={14} /> 1. Close period {periodKey}
            </p>
            <p className="text-xs text-[#A1A1AA] mt-1">
              Freezes rankings + the {formatCents(poolCents)} pool. Tips after this moment don&apos;t count.
            </p>
          </div>
          {isOpen && confirming !== 'close' && (
            <button
              onClick={() => setConfirming('close')}
              disabled={busy !== null}
              className="btn-primary !w-auto !px-4 !py-2 text-sm"
              data-testid="admin-close-btn"
            >
              Close period
            </button>
          )}
          {isOpen && confirming === 'close' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setConfirming(null)}
                className="text-xs text-[#A1A1AA] hover:text-white px-2"
                data-testid="admin-close-cancel"
              >Cancel</button>
              <button
                onClick={runClose}
                disabled={busy !== null}
                className="btn-primary !w-auto !px-4 !py-2 text-sm bg-red-500/90 hover:bg-red-500"
                data-testid="admin-close-confirm"
              >
                {busy === 'close' ? <Loader2 className="animate-spin" size={14} /> : 'Confirm close'}
              </button>
            </div>
          )}
          {!isOpen && (
            <span className="text-xs text-green-300 flex items-center gap-1"
                  data-testid="admin-close-done">
              <CheckCircle size={14} /> Closed
            </span>
          )}
        </div>
      </div>

      {/* Step 2: Payout */}
      <div className="rounded-xl bg-black/40 border border-white/5 p-4" data-testid="admin-payout-block">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-semibold text-sm flex items-center gap-1.5">
              <DollarSign size={14} /> 2. Pay out via Stripe Transfer
            </p>
            <p className="text-xs text-[#A1A1AA] mt-1">
              Requires the period to be closed. Winners without payouts enabled are skipped and their share reallocates to the next eligible worker.
            </p>
          </div>
          {isClosed && confirming !== 'payout' && (
            <button
              onClick={() => setConfirming('payout')}
              disabled={busy !== null}
              className="btn-primary !w-auto !px-4 !py-2 text-sm"
              data-testid="admin-payout-btn"
            >
              Pay winners
            </button>
          )}
          {isClosed && confirming === 'payout' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setConfirming(null)}
                className="text-xs text-[#A1A1AA] hover:text-white px-2"
                data-testid="admin-payout-cancel"
              >Cancel</button>
              <button
                onClick={runPayout}
                disabled={busy !== null}
                className="btn-primary !w-auto !px-4 !py-2 text-sm bg-red-500/90 hover:bg-red-500"
                data-testid="admin-payout-confirm"
              >
                {busy === 'payout' ? <Loader2 className="animate-spin" size={14} /> : 'Confirm & transfer'}
              </button>
            </div>
          )}
          {isOpen && (
            <span className="text-xs text-[#52525B]">Close the period first</span>
          )}
          {isFinal && (
            <span
              className={`text-xs flex items-center gap-1 ${status === 'paid' ? 'text-green-300' : 'text-amber-300'}`}
              data-testid="admin-payout-done"
            >
              {status === 'paid' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
              {status === 'paid' ? 'Paid' : status === 'partially_paid' ? 'Partially paid' : 'Payout failed'}
            </span>
          )}
        </div>
      </div>

      {lastResult?.kind === 'payout' && lastResult.period?.payoutRecords && (
        <div className="mt-4 rounded-xl bg-black/40 border border-white/5 p-4"
             data-testid="admin-payout-summary">
          <p className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Payout summary</p>
          {lastResult.period.payoutRecords.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 text-sm border-b border-white/5 last:border-b-0">
              <div className="min-w-0 truncate">
                <span className="font-semibold">{r.displayName || r.userId}</span>
                {r.originalRank && <span className="text-xs text-[#52525B] ml-2">#{r.originalRank}</span>}
              </div>
              <div className="text-right">
                <span className="font-mono mr-3">{formatCents(r.amountCents)}</span>
                <span className={
                  r.status === 'paid' ? 'text-xs text-green-300' :
                  r.status === 'skipped' ? 'text-xs text-amber-300' :
                  'text-xs text-red-300'
                }>{r.status}{r.reason ? ` · ${r.reason}` : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function LeaderboardPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { ok, data } = await xhrRequest('GET',
      `${process.env.REACT_APP_BACKEND_URL}/api/challenge/current`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (ok) setData(data);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#F5C542]" size={32} />
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#050505] p-8 text-white">Could not load leaderboard.</div>;
  }

  const isPodium = data.phase === 'podium';
  const anyoneQualified = data.topEarner.length > 0 || data.highestRate.length > 0;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="leaderboard-page">
      <header className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-xl border-b border-white/5 px-4 py-3 md:hidden">
        <h1 className="font-heading font-bold text-lg text-white flex items-center gap-2">
          <Trophy size={20} className="text-[#F5C542]" />
          Top Earner Challenge
        </h1>
      </header>

      <main className="p-4 md:p-8 pb-24 md:pb-8 max-w-3xl mx-auto space-y-6">
        <div className="hidden md:flex md:items-start md:justify-between">
          <div>
            <h2 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Trophy className="text-[#F5C542]" size={28} />
              Top Earner Challenge
            </h2>
            <p className="text-[#A1A1AA] mt-1">
              Every month, the top service pros on Golden Tips split a pool funded by 1% of every tip.
            </p>
          </div>
          <Link
            to="/dashboard/leaderboard/history"
            className="text-sm text-[#A1A1AA] hover:text-white flex items-center gap-1.5"
            data-testid="leaderboard-history-link-desktop"
          >
            <History size={16} /> Past challenges
          </Link>
        </div>

        {/* Pool + phase hero */}
        <div className="bg-gradient-to-br from-[#F5C542]/10 to-transparent border border-[#F5C542]/30 rounded-2xl p-6" data-testid="pool-hero">
          <p className="text-xs uppercase tracking-widest text-[#F5C542]/80">This month&apos;s pool</p>
          <p
            className="text-5xl font-heading font-bold text-[#F5C542] mt-1"
            data-testid="pool-amount"
          >
            {formatCents(data.poolCents)}
          </p>
          <div className="mt-4 flex items-start gap-2 text-sm">
            <Sparkles size={16} className="text-[#F5C542] flex-shrink-0 mt-0.5" />
            <p className="text-white/80" data-testid="phase-copy">
              {isPodium ? (
                <>Top 3 in each category are paid out. 70% goes to Top Earner, 30% to Highest Tip Rate — each split 50/30/20 across the podium.</>
              ) : (
                <>Winner-take-all this month. The #1 Top Earner takes the whole pool when the period closes. Once the pool passes {formatCents(data.tierThresholdCents)}, Top 3 in each category start getting paid.</>
              )}
            </p>
          </div>
        </div>

        {data.meIsAdmin && (
          <AdminPanel
            periodKey={data.periodKey}
            poolCents={data.poolCents}
            status={data.status || 'open'}
            onDone={load}
          />
        )}

        {!anyoneQualified && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center" data-testid="empty-state">
            <Info size={24} className="text-[#A1A1AA] mx-auto mb-2" />
            <p className="font-semibold">No one qualifies yet.</p>
            <p className="text-sm text-[#A1A1AA] mt-1">
              Workers need at least {data.minUniqueTippers} tips this month to appear on the leaderboard.
            </p>
          </div>
        )}

        {/* Top Earner category */}
        {data.topEarner.length > 0 && (
          <section className="stat-card" data-testid="category-top-earner">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#F5C542]/10 flex items-center justify-center">
                <Trophy size={20} className="text-[#F5C542]" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Top Earner</h3>
                <p className="text-xs text-[#A1A1AA]">By total tips received this month</p>
              </div>
            </div>
            <div>
              {data.topEarner.map((e) => (
                <RankRow key={e.userId} entry={e} showPayout={isPodium || e.rank === 1} />
              ))}
            </div>
          </section>
        )}

        {/* Highest Tip Rate category */}
        {data.highestRate.length > 0 && (
          <section className="stat-card" data-testid="category-highest-rate">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                <TrendingUp size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Highest Tip Rate</h3>
                <p className="text-xs text-[#A1A1AA]">By average tip size (quality over quantity)</p>
              </div>
            </div>
            <div>
              {data.highestRate.map((e) => (
                <RankRow key={e.userId} entry={e} showPayout={isPodium} />
              ))}
            </div>
            {!isPodium && (
              <p className="text-xs text-[#52525B] mt-3 italic">
                No cash payout this month — this ranking earns a badge only until the pool passes {formatCents(data.tierThresholdCents)}.
              </p>
            )}
          </section>
        )}

        <Link
          to="/dashboard/leaderboard/history"
          className="md:hidden flex items-center justify-center gap-2 text-sm text-[#A1A1AA] hover:text-white py-3"
          data-testid="leaderboard-history-link-mobile"
        >
          <History size={16} /> View past challenges
        </Link>

        <Footer className="mt-6" />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/5 px-4 py-2 flex items-center justify-around z-50">
        <Link to="/dashboard" className="flex flex-col items-center gap-1 py-2 px-3 text-[#A1A1AA]">
          <Home size={20} /><span className="text-xs">Home</span>
        </Link>
        <Link to="/dashboard/tips" className="flex flex-col items-center gap-1 py-2 px-3 text-[#A1A1AA]">
          <List size={20} /><span className="text-xs">Tips</span>
        </Link>
        <Link to="/dashboard/leaderboard" className="flex flex-col items-center gap-1 py-2 px-3 text-[#F5C542]">
          <Trophy size={20} /><span className="text-xs">Challenge</span>
        </Link>
        <Link to="/dashboard/settings" className="flex flex-col items-center gap-1 py-2 px-3 text-[#A1A1AA]">
          <User size={20} /><span className="text-xs">Settings</span>
        </Link>
      </nav>
    </div>
  );
}
